// AVIV API - proxy API calls for tile-api, price-tile-api, and place-api

import HTTPService from '../services/HTTPService.es6';

import { MAP_MAX_ZOOM, MAP_MIN_ZOOM } from './aviv-config';

function getProxyBaseUrl() {
    return `${window.location.protocol}//${window.location.host}`;
}

function getFallbackPriceDate() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

export async function getActivePriceDate(country = 'FR') {
    const url = `/api/tiles/price/active-date/${country}`;

    try {
        const response = await fetch(url);
        if (!response.ok) {
            global.console.warn(`[AVIV] GET ${url} failed with status ${response.status}`);
            return getFallbackPriceDate();
        }
        const data = await response.json();
        return data.active_price_date;
    } catch (error) {
        global.console.warn(`[AVIV] GET ${url} failed:`, error.message);
        return getFallbackPriceDate();
    }
}

export async function getGeoLayerMetadata() {
    const url = '/api/tiles/geo/metadata';

    try {
        const response = await fetch(url);
        if (!response.ok) {
            global.console.warn(`[AVIV] GET ${url} failed with status ${response.status}`);
            return [];
        }
        const data = await response.json();

        const layers = data.layers.map(layer => ({
            name: layer.name,
            type: layer.type,
            description: layer.description,
            minzoom: MAP_MIN_ZOOM,
            maxzoom: MAP_MAX_ZOOM,
            tiles: [`${getProxyBaseUrl()}/api/tiles/geo/layer/${layer.type}/{z}/{x}/{y}.pbf`],
            place: { type: layer.type },
        }));
        return layers;
    } catch (error) {
        global.console.warn(`[AVIV] GET ${url} failed:`, error.message);
        return [];
    }
}

export async function getPriceLayerMetadata(transactionType, boundaryPlaceId, priceDate) {
    const url = `/api/tiles/price/metadata/${transactionType}/${boundaryPlaceId}/${priceDate}`;

    try {
        const response = await fetch(url);
        if (!response.ok) {
            global.console.warn(`[AVIV] GET ${url} failed with status ${response.status}`);
            return [];
        }
        const data = await response.json();

        const layers = data.map(layer => ({
            ...layer,
            tiles: layer.tiles
                ? layer.tiles.map(tileUrl => {
                      const pathWithoutV1 = tileUrl.replace(/^\/v1\//, '/');
                      return `${getProxyBaseUrl()}/api/tiles/price${pathWithoutV1}`;
                  })
                : [],
            place: layer.place || { type: layer.name.split('__')[1] || layer.name },
            boundaryPlace: layer.boundary_place
                ? {
                      id: layer.boundary_place.id,
                      colorScale: layer.boundary_place.color_scale,
                      colorScaleStr: layer.boundary_place.colorScaleStr,
                  }
                : layer.boundaryPlace,
        }));
        return layers;
    } catch (error) {
        global.console.warn(`[AVIV] GET ${url} failed:`, error.message);
        return [];
    }
}

function slugify(displayName) {
    return displayName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

function extractSlugFromNames(names) {
    const firstEntry = names?.fr?.[0];
    if (firstEntry?.slug) return firstEntry.slug;
    if (firstEntry?.display_name) return slugify(firstEntry.display_name);
    return null;
}

function buildAddressUrl(item, streetParent, cityParent) {
    const citySlug = cityParent ? extractSlugFromNames(cityParent.names) : null;
    const streetSlug = streetParent ? extractSlugFromNames(streetParent.names) : null;
    const streetLegacyId = streetParent?.mapping?.meilleursagents?.[0]?.id;
    const addressSlug = item?.names?.fr?.[0]?.slug || null;

    if (!streetSlug || !streetLegacyId) {
        return null;
    }

    return HTTPService.getPlaceURL({
        city_slug: citySlug,
        street_slug: streetSlug,
        street_id: streetLegacyId,
        address_slug: addressSlug || '',
    });
}

export async function getAvivIdFromLegacy(legacyType, legacyId) {
    const url = `/api/tiles/places/legacy/${legacyType}/${legacyId}`;

    try {
        const response = await fetch(url);
        if (!response.ok) {
            global.console.warn(`[AVIV] GET ${url} failed with status ${response.status}`);
            return null;
        }
        const data = await response.json();
        return data?.item?.id ?? null;
    } catch (error) {
        global.console.warn(`[AVIV] GET ${url} failed:`, error.message);
        return null;
    }
}

export async function getPlaceData(placeId) {
    const url = `/api/tiles/places/${placeId}`;

    try {
        const response = await fetch(url);
        if (!response.ok) {
            global.console.warn(`[AVIV] GET ${url} failed with status ${response.status}`);
            return null;
        }
        const data = await response.json();
        const { item } = data;

        const slug = extractSlugFromNames(item?.names);
        const name = item?.names?.fr?.[0]?.display_name || null;
        return {
            ...item,
            slug,
            name,
        };
    } catch (error) {
        global.console.warn(`[AVIV] GET ${url} failed:`, error.message);
        return null;
    }
}

export async function getLinkedAddresses(placeId) {
    // Use AVIV place-api to get linked addresses
    const url = `/api/tiles/places/${placeId}/children?place_type=HONU&parent_types=STRT&parent_types=AD08`;

    try {
        const response = await fetch(url);
        if (!response.ok) {
            global.console.warn(`[AVIV] GET ${url} failed with status ${response.status}`);
            return [];
        }
        const data = await response.json();
        const items = data?.items || [];

        const addresses = items
            .filter(item => item.active)
            .map(item => {
                const streetParent = item.parents?.find(p => p.type_key === 'STRT');
                const cityParent = item.parents?.find(p => p.type_key === 'AD08');

                // Build display name like legacy: "{houseNumber} {streetName}" or just "{streetName}" for lieu-dits
                const addressName = item?.names?.fr?.[0]?.name;
                const streetNames = streetParent?.names?.fr?.[0];
                const streetName = streetNames?.name || streetNames?.display_name || null;
                const name =
                    addressName && streetName
                        ? `${addressName} ${streetName}`
                        : streetName || item?.names?.fr?.[0]?.display_name || null;

                return {
                    ...item,
                    name,
                    url: buildAddressUrl(item, streetParent, cityParent),
                };
            })
            .filter(item => item.url);

        return addresses;
    } catch (error) {
        global.console.warn(`[AVIV] GET ${url} failed:`, error.message);
        return [];
    }
}
